import { z } from 'zod';
import { CountryCodeSchema, UNSERVICEABLE_REASONS, TradingStateSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Search + discovery (W8)                                             */
/*                                                                     */
/* Customers find restaurants by where they are: only outlets inside   */
/* the delivery radius are shown, the radius shrinks at peak, late at  */
/* night and in rain, and results are ranked by a weighted score whose */
/* weights live in the settings registry (group "discovery").          */
/* ------------------------------------------------------------------ */

/** Town-wide delivery mode set by Ops. Rain and peak shrink the radius; rain and festival add time. */
export const DELIVERY_MODES = ['normal', 'peak', 'rain', 'festival'] as const;
export const DeliveryModeSchema = z.enum(DELIVERY_MODES);
export type DeliveryMode = z.infer<typeof DeliveryModeSchema>;

export const DELIVERY_MODE_LABELS: Record<DeliveryMode, string> = {
  normal: 'Normal',
  peak: 'Peak',
  rain: 'Rain',
  festival: 'Festival',
};

export const PRICE_BANDS = ['low', 'mid', 'high'] as const;
export const PriceBandSchema = z.enum(PRICE_BANDS);
export type PriceBand = z.infer<typeof PriceBandSchema>;

export const SEARCH_SORTS = [
  'relevance',
  'distance',
  'rating',
  'eta',
  'cost_low',
  'cost_high',
] as const;
export const SearchSortSchema = z.enum(SEARCH_SORTS);
export type SearchSort = z.infer<typeof SearchSortSchema>;

export const SEARCH_SORT_LABELS: Record<SearchSort, string> = {
  relevance: 'Relevance',
  distance: 'Distance',
  rating: 'Rating',
  eta: 'Delivery time',
  cost_low: 'Cost: low to high',
  cost_high: 'Cost: high to low',
};

export const SEARCH_ENGINES = ['meilisearch', 'postgres'] as const;
export type SearchEngineName = (typeof SEARCH_ENGINES)[number];

/** Query-string boolean: only the literal "true" is on. */
const queryFlag = z
  .enum(['true', 'false'])
  .optional()
  .transform((value) => value === 'true');

const cuisineText = z.string().trim().min(2).max(40);

/** Filters as they arrive in a query string. */
export const DiscoveryFilterQuerySchema = z.object({
  /** Outlets that serve vegetarian food (veg or both). */
  veg: queryFlag,
  /** Only fully vegetarian outlets. */
  pureVeg: queryFlag,
  rating4: queryFlag,
  /** Estimated delivery within the "fast" minutes setting. */
  fast: queryFlag,
  /** Outlets with a live offer. Offers arrive with the Offer Engine wave; until then it is ignored. */
  offers: queryFlag,
  priceBand: PriceBandSchema.optional(),
  cuisine: cuisineText.optional(),
  newOnly: queryFlag,
  openNow: queryFlag,
});
export type DiscoveryFilterQuery = z.infer<typeof DiscoveryFilterQuerySchema>;

/** Filters as stored on a rule-based collection (JSON, not a query string). */
export const DiscoveryFiltersSchema = z.object({
  veg: z.boolean().default(false),
  pureVeg: z.boolean().default(false),
  rating4: z.boolean().default(false),
  fast: z.boolean().default(false),
  offers: z.boolean().default(false),
  priceBand: PriceBandSchema.nullable().default(null),
  cuisine: cuisineText.nullable().default(null),
  newOnly: z.boolean().default(false),
  openNow: z.boolean().default(false),
});
export type DiscoveryFilters = z.infer<typeof DiscoveryFiltersSchema>;

export const EMPTY_DISCOVERY_FILTERS: DiscoveryFilters = {
  veg: false,
  pureVeg: false,
  rating4: false,
  fast: false,
  offers: false,
  priceBand: null,
  cuisine: null,
  newOnly: false,
  openNow: false,
};

const lat = z.coerce.number().min(-90).max(90);
const lng = z.coerce.number().min(-180).max(180);
const searchText = z.string().trim().max(60).optional();
const limit = z.coerce.number().int().min(1).max(50).default(30);

/** Customer search (public, guest browsing allowed). */
export const DiscoverySearchQuerySchema = DiscoveryFilterQuerySchema.extend({
  lat,
  lng,
  q: searchText,
  sort: SearchSortSchema.default('relevance'),
  limit,
});
export type DiscoverySearchQuery = z.infer<typeof DiscoverySearchQuerySchema>;

/** Capsico OS search tester: same search plus overrides and the score breakdown. */
export const DiscoveryTestQuerySchema = DiscoverySearchQuerySchema.extend({
  /** Search a town even when the point is outside every live zone. */
  townId: z.uuid().optional(),
  /** Preview another delivery mode than the one set for the town. */
  mode: DeliveryModeSchema.optional(),
  /** Preview another moment (ISO time) — changes open/closed, late night and rush hour. */
  at: z.iso.datetime({ offset: true }).optional(),
});
export type DiscoveryTestQuery = z.infer<typeof DiscoveryTestQuerySchema>;

export const FeedQuerySchema = z.object({ lat, lng });
export type FeedQuery = z.infer<typeof FeedQuerySchema>;

/* ---------------- results ---------------- */

export const RankSignalKeySchema = z.enum([
  'distance',
  'eta',
  'rating',
  'popularity',
  'conversion',
  'local_legend',
  'personal',
  'text',
]);
export type RankSignalKey = z.infer<typeof RankSignalKeySchema>;

export const RANK_SIGNAL_LABELS: Record<RankSignalKey, string> = {
  distance: 'Distance',
  eta: 'Delivery time',
  rating: 'Rating',
  popularity: 'Popularity',
  conversion: 'Conversion',
  local_legend: 'Local Legend',
  personal: 'Personal history',
  text: 'Matches the search',
};

export const RankBreakdownSchema = z.object({
  signals: z.array(
    z.object({
      key: RankSignalKeySchema,
      /** 0–1, or null when there is no data yet (the signal is then left out). */
      value: z.number().nullable(),
      weight: z.number(),
      /** Share of the final score this signal contributed (0–100 scale). */
      points: z.number(),
    }),
  ),
  multipliers: z.array(
    z.object({
      key: z.enum(['new_listing', 'rejections', 'reaudit', 'integrity']),
      label: z.string(),
      factor: z.number(),
    }),
  ),
  base: z.number(),
  final: z.number(),
});
export type RankBreakdown = z.infer<typeof RankBreakdownSchema>;

export const EtaRangeSchema = z.object({
  minutes: z.number().int(),
  from: z.number().int(),
  to: z.number().int(),
});
export type EtaRange = z.infer<typeof EtaRangeSchema>;

export const MatchedDishSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  priceMinor: z.number().int(),
  foodType: z.enum(['veg', 'non_veg', 'egg']),
});
export type MatchedDish = z.infer<typeof MatchedDishSchema>;

export const OutletCardSchema = z.object({
  outletId: z.uuid(),
  restaurantId: z.uuid(),
  name: z.string(),
  restaurantName: z.string(),
  locality: z.string(),
  cuisines: z.array(z.string()),
  foodType: z.enum(['veg', 'non_veg', 'both']),
  distanceKm: z.number(),
  eta: EtaRangeSchema,
  rating: z.object({ average: z.number().nullable(), count: z.number().int() }),
  costForTwoMinor: z.number().int().nullable(),
  /** 16:9 cover photo (small size), when the outlet has one. */
  coverUrl: z.string().nullable(),
  localLegend: z.boolean(),
  isNew: z.boolean(),
  /** W17 public hygiene badge (absent / null when hidden or trust is off). */
  hygieneBadge: z.enum(['green', 'amber']).nullable().optional(),
  open: z.boolean(),
  /** Plain words for a greyed-out card ("Opens at 11:00 AM"). */
  closedReason: z.string().nullable(),
  matchedDishes: z.array(MatchedDishSchema),
  score: z.number(),
  /** Only in Capsico OS (search tester). */
  breakdown: RankBreakdownSchema.optional(),
});
export type OutletCard = z.infer<typeof OutletCardSchema>;

export const DiscoveryAreaSchema = z.object({
  serviceable: z.boolean(),
  reason: z.enum(UNSERVICEABLE_REASONS).nullable(),
  country: CountryCodeSchema.nullable(),
  town: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  zone: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  trading: TradingStateSchema.nullable(),
  mode: DeliveryModeSchema,
  /** Delivery radius used for this search, and why ("Rain mode", "Late night"). */
  radiusKm: z.number(),
  radiusReason: z.string(),
  night: z.boolean(),
});
export type DiscoveryArea = z.infer<typeof DiscoveryAreaSchema>;

export const FacetSchema = z.object({ name: z.string(), count: z.number().int() });
export type Facet = z.infer<typeof FacetSchema>;

export const DiscoverySearchResultSchema = z.object({
  engine: z.enum(SEARCH_ENGINES),
  tookMs: z.number().int(),
  area: DiscoveryAreaSchema,
  query: z.object({ text: z.string(), variants: z.array(z.string()) }),
  /** Filters accepted but not applied yet (e.g. offers before the Offer Engine). */
  ignoredFilters: z.array(z.string()),
  total: z.number().int(),
  results: z.array(OutletCardSchema),
  cuisines: z.array(FacetSchema),
});
export type DiscoverySearchResult = z.infer<typeof DiscoverySearchResultSchema>;

export const FEED_ROW_KINDS = ['top', 'new', 'legends', 'collection'] as const;

export const FeedRowSchema = z.object({
  key: z.string(),
  kind: z.enum(FEED_ROW_KINDS),
  title: z.string(),
  subtitle: z.string().nullable(),
  collectionId: z.uuid().nullable(),
  items: z.array(OutletCardSchema),
});
export type FeedRow = z.infer<typeof FeedRowSchema>;

/** "What's on your mind?" — dishes many nearby kitchens serve; tapping one searches for it. */
export const MindTileSchema = z.object({
  name: z.string(),
  /** Search text to run when tapped. */
  query: z.string(),
  imageUrl: z.string().nullable(),
  outlets: z.number().int(),
});
export type MindTile = z.infer<typeof MindTileSchema>;

export const DiscoveryFeedSchema = z.object({
  area: DiscoveryAreaSchema,
  rows: z.array(FeedRowSchema),
  cuisines: z.array(FacetSchema),
  mind: z.array(MindTileSchema),
});
export type DiscoveryFeed = z.infer<typeof DiscoveryFeedSchema>;

/* ---------------- collections ---------------- */

export const COLLECTION_KINDS = ['manual', 'rule'] as const;
export const CollectionKindSchema = z.enum(COLLECTION_KINDS);
export type CollectionKind = z.infer<typeof CollectionKindSchema>;

export const COLLECTION_STATUSES = ['draft', 'live', 'hidden'] as const;
export const CollectionStatusSchema = z.enum(COLLECTION_STATUSES);
export type CollectionStatus = z.infer<typeof CollectionStatusSchema>;

export const CollectionRuleSchema = z.object({
  filters: DiscoveryFiltersSchema,
  sort: SearchSortSchema.default('relevance'),
  limit: z.number().int().min(3).max(30).default(12),
});
export type CollectionRule = z.infer<typeof CollectionRuleSchema>;

const collectionFields = {
  country: CountryCodeSchema,
  /** null = every town in the country. */
  townId: z.uuid().nullable(),
  title: z.string().trim().min(3, 'At least 3 characters').max(60),
  subtitle: z.string().trim().max(120).nullable(),
  kind: CollectionKindSchema,
  rule: CollectionRuleSchema.nullable(),
  /** Hand-picked outlets in display order (manual collections). */
  outletIds: z.array(z.uuid()).max(50),
  status: CollectionStatusSchema,
  startsAt: z.iso.datetime({ offset: true }).nullable(),
  endsAt: z.iso.datetime({ offset: true }).nullable(),
  sortOrder: z.number().int().min(0).max(999),
};

const collectionChecks = <
  T extends z.ZodType<{
    kind: CollectionKind;
    rule: CollectionRule | null;
    outletIds: string[];
    startsAt: string | null;
    endsAt: string | null;
  }>,
>(
  schema: T,
) =>
  schema
    .refine((value) => value.kind !== 'rule' || value.rule !== null, {
      message: 'A rule collection needs its filters',
      path: ['rule'],
    })
    .refine((value) => value.kind !== 'manual' || value.outletIds.length > 0, {
      message: 'Pick at least one restaurant',
      path: ['outletIds'],
    })
    .refine(
      (value) =>
        value.startsAt === null ||
        value.endsAt === null ||
        Date.parse(value.startsAt) < Date.parse(value.endsAt),
      { message: 'The end must be after the start', path: ['endsAt'] },
    );

export const CreateCollectionInputSchema = collectionChecks(z.object(collectionFields));
export type CreateCollectionInput = z.infer<typeof CreateCollectionInputSchema>;

export const UpdateCollectionInputSchema = collectionChecks(
  z.object({ ...collectionFields, expectedVersion: z.number().int().min(1) }),
);
export type UpdateCollectionInput = z.infer<typeof UpdateCollectionInputSchema>;

export const CollectionViewSchema = z.object({
  id: z.uuid(),
  country: CountryCodeSchema,
  townId: z.uuid().nullable(),
  townName: z.string().nullable(),
  title: z.string(),
  subtitle: z.string().nullable(),
  kind: CollectionKindSchema,
  rule: CollectionRuleSchema.nullable(),
  outlets: z.array(
    z.object({ id: z.uuid(), name: z.string(), townName: z.string(), live: z.boolean() }),
  ),
  status: CollectionStatusSchema,
  /** live and inside its date window right now. */
  showing: z.boolean(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  sortOrder: z.number().int(),
  version: z.number().int(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});
export type CollectionView = z.infer<typeof CollectionViewSchema>;

export const CollectionListQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  townId: z.uuid().optional(),
});
export type CollectionListQuery = z.infer<typeof CollectionListQuerySchema>;

/* ---------------- synonyms ---------------- */

export const SYNONYM_KINDS = ['synonym', 'spelling'] as const;
export const SynonymKindSchema = z.enum(SYNONYM_KINDS);
export type SynonymKind = z.infer<typeof SynonymKindSchema>;

const synonymWord = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .transform((value) => value.toLowerCase());

export const SynonymInputSchema = z
  .object({
    /** null = both countries. */
    country: CountryCodeSchema.nullable(),
    term: synonymWord,
    synonyms: z.array(synonymWord).min(1, 'Add at least one other word').max(10),
    kind: SynonymKindSchema,
  })
  .refine((value) => !value.synonyms.includes(value.term), {
    message: 'A word cannot be its own synonym',
    path: ['synonyms'],
  });
export type SynonymInput = z.infer<typeof SynonymInputSchema>;

export const UpdateSynonymInputSchema = SynonymInputSchema.and(
  z.object({ expectedVersion: z.number().int().min(1) }),
);
export type UpdateSynonymInput = z.infer<typeof UpdateSynonymInputSchema>;

export const SynonymViewSchema = z.object({
  id: z.uuid(),
  country: CountryCodeSchema.nullable(),
  term: z.string(),
  synonyms: z.array(z.string()),
  kind: SynonymKindSchema,
  version: z.number().int(),
  updatedAt: z.string(),
});
export type SynonymView = z.infer<typeof SynonymViewSchema>;

/* ---------------- delivery modes ---------------- */

export const SetDeliveryModeInputSchema = z.object({
  mode: DeliveryModeSchema,
  /** null = until changed back by hand. */
  durationMinutes: z.number().int().min(15).max(1440).nullable(),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(120),
  /** null when the town has never had a mode set. */
  expectedVersion: z.number().int().min(1).nullable(),
});
export type SetDeliveryModeInput = z.infer<typeof SetDeliveryModeInputSchema>;

export const TownModeViewSchema = z.object({
  townId: z.uuid(),
  townName: z.string(),
  country: CountryCodeSchema,
  townStatus: z.string(),
  mode: DeliveryModeSchema,
  until: z.string().nullable(),
  reason: z.string().nullable(),
  setBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
  version: z.number().int().nullable(),
  /** Radius customers get right now, and why. */
  radiusKm: z.number(),
  radiusReason: z.string(),
  night: z.boolean(),
});
export type TownModeView = z.infer<typeof TownModeViewSchema>;

/* ---------------- listings (per-outlet discovery settings) ---------------- */

export const ListingQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  townId: z.uuid().optional(),
  q: z.string().trim().max(60).optional(),
  legendOnly: queryFlag,
});
export type ListingQuery = z.infer<typeof ListingQuerySchema>;

export const ListingRowSchema = z.object({
  outletId: z.uuid(),
  name: z.string(),
  restaurantName: z.string(),
  townId: z.uuid(),
  townName: z.string(),
  country: CountryCodeSchema,
  status: z.string(),
  liveDishes: z.number().int(),
  localLegend: z.boolean(),
  legendSince: z.string().nullable(),
  /** Outlet's own delivery radius cap; null = town radius only. */
  radiusKm: z.number().nullable(),
  version: z.number().int().nullable(),
  stats: z.object({
    delivered30d: z.number().int(),
    orders7d: z.number().int(),
    rejected7d: z.number().int(),
    avgPrepMinutes: z.number().nullable(),
    ratingAverage: z.number().nullable(),
    ratingCount: z.number().int(),
    costForTwoMinor: z.number().int().nullable(),
    refreshedAt: z.string().nullable(),
  }),
  searchable: z.boolean(),
});
export type ListingRow = z.infer<typeof ListingRowSchema>;

export const UpdateListingInputSchema = z.object({
  localLegend: z.boolean(),
  radiusKm: z.number().min(0.5).max(30).nullable(),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(120),
  /** null when the outlet has no listing settings yet. */
  expectedVersion: z.number().int().min(1).nullable(),
});
export type UpdateListingInput = z.infer<typeof UpdateListingInputSchema>;

/* ---------------- index + insights ---------------- */

export const SearchIndexStatusSchema = z.object({
  engine: z.enum(SEARCH_ENGINES),
  /** Meilisearch configured in env. */
  configured: z.boolean(),
  /** null when not configured or not checked. */
  healthy: z.boolean().nullable(),
  documents: z.object({
    outlets: z.number().int().nullable(),
    dishes: z.number().int().nullable(),
  }),
  lastSyncAt: z.string().nullable(),
  lastFullAt: z.string().nullable(),
  lastError: z.string().nullable(),
  statsRefreshedAt: z.string().nullable(),
  searchableOutlets: z.number().int(),
});
export type SearchIndexStatus = z.infer<typeof SearchIndexStatusSchema>;

export const RefreshInputSchema = z.object({ full: z.boolean().default(false) });
export type RefreshInput = z.infer<typeof RefreshInputSchema>;

export const RefreshResultSchema = z.object({
  statsOutlets: z.number().int(),
  index: z.object({
    engine: z.enum(SEARCH_ENGINES),
    skipped: z.boolean(),
    full: z.boolean(),
    upsertedOutlets: z.number().int(),
    upsertedDishes: z.number().int(),
    removedOutlets: z.number().int(),
    error: z.string().nullable(),
  }),
  tookMs: z.number().int(),
});
export type RefreshResult = z.infer<typeof RefreshResultSchema>;

export const InsightsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(7),
  country: CountryCodeSchema.optional(),
  townId: z.uuid().optional(),
});
export type InsightsQuery = z.infer<typeof InsightsQuerySchema>;

export const SearchInsightsSchema = z.object({
  days: z.number().int(),
  searches: z.number().int(),
  zeroResults: z.number().int(),
  fallbackSearches: z.number().int(),
  avgTookMs: z.number().nullable(),
  topQueries: z.array(
    z.object({ query: z.string(), count: z.number().int(), avgResults: z.number() }),
  ),
  zeroQueries: z.array(
    z.object({ query: z.string(), count: z.number().int(), lastAt: z.string() }),
  ),
});
export type SearchInsights = z.infer<typeof SearchInsightsSchema>;

/** The resolved ranking weights for a scope (read-only view for Capsico OS). */
export const RankingWeightsSchema = z.object({
  weights: z.record(RankSignalKeySchema, z.number()),
  ratingPrior: z.number(),
  ratingMinCount: z.number().int(),
  fastMinutes: z.number().int(),
  newListingDays: z.number().int(),
  newListingBoost: z.number(),
  rejectLimitPercent: z.number(),
  rejectPenaltyPercent: z.number(),
});
export type RankingWeights = z.infer<typeof RankingWeightsSchema>;
